import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, CheckCircle, DollarSign, MessageSquare, ArrowUpRight, ArrowDownRight,
  ArrowRight, Star, Shield, X, Globe, Briefcase, Bell,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { GlassCard, API } from './GlassCard';
import VendorEarningsPulse from './VendorEarningsPulse';

const POLL_MS = 20_000; // live-feeling dashboard

export default function Overview() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [completion, setCompletion] = useState(null);
  // Pending inquiry + custom-order counts (drives the gold flashing badge)
  const [pendingInquiries, setPendingInquiries] = useState(0);
  const [pendingCustomOrders, setPendingCustomOrders] = useState(0);

  useEffect(() => {
    fetchData();
    checkWebsite();
    fetchCompletion();
    fetchInquiryCounts();
    // Live updates — poll lightly so the dashboard feels alive without
    // hammering the backend.
    const id = setInterval(() => { fetchData(); fetchInquiryCounts(); }, POLL_MS);
    const onFocus = () => { fetchData(); fetchInquiryCounts(); };
    const onRefresh = () => { fetchData(); fetchInquiryCounts(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('vendor-stats-refresh', onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('vendor-stats-refresh', onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInquiryCounts = async () => {
    try {
      const [iq, co] = await Promise.all([
        fetch(`${API}/messages/inquiries`, { headers: getAuthHeaders() }),
        fetch(`${API}/custom-orders/mine?role=vendor`, { headers: getAuthHeaders() }),
      ]);
      if (iq.ok) {
        const d = await iq.json();
        const pending = (d.inquiries || []).filter(
          (i) => i.inquiry_meta?.status !== 'accepted' && i.inquiry_meta?.status !== 'declined',
        );
        setPendingInquiries(pending.length);
      }
      if (co.ok) {
        const d = await co.json();
        const open = (d.orders || []).filter((o) =>
          ['pending_payment', 'active', 'revision_requested'].includes(o.status),
        );
        setPendingCustomOrders(open.length);
      }
    } catch {}
  };

  const fetchCompletion = async () => {
    try {
      const res = await fetch(`${API}/completion/vendor`, { headers: getAuthHeaders() });
      if (res.ok) setCompletion(await res.json());
    } catch {}
  };

  const checkWebsite = async () => {
    try {
      const response = await fetch(`${API}/builder/my-site`, { headers: getAuthHeaders() });
      if (response.ok) return; // has site
      if (localStorage.getItem('dismissed_website_trial') !== 'true') setShowTrialPrompt(true);
    } catch {
      if (localStorage.getItem('dismissed_website_trial') !== 'true') setShowTrialPrompt(true);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, vendorRes, bookingsRes] = await Promise.all([
        fetch(`${API}/stats/vendor`, { headers: getAuthHeaders() }),
        fetch(`${API}/vendors/me`, { headers: getAuthHeaders() }),
        fetch(`${API}/bookings/my-bookings?role=vendor`, { headers: getAuthHeaders() }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (vendorRes.ok) setVendor(await vendorRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const stepRoutes = {
    profile_picture: '/dashboard/vendor/profile',
    ethnicity: '/dashboard/vendor/profile',
    description: '/dashboard/vendor/profile',
    location: '/dashboard/vendor/profile',
    first_service: '/dashboard/vendor/services/new',
    service_photos: '/dashboard/vendor/services',
    portfolio: '/dashboard/vendor/profile',
    stripe_connected: '/dashboard/vendor/payments',
    bio: '/dashboard/vendor/profile',
    banner_image: '/dashboard/vendor/profile',
  };

  return (
    <div className="space-y-8">
      {showTrialPrompt && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative p-5 rounded-2xl bg-gradient-to-r from-[#8B5CF6]/20 via-[#3B82F6]/10 to-[#EC4899]/20 border border-[#8B5CF6]/30 shadow-[0_0_30px_rgba(139,92,246,0.2)]" data-testid="website-trial-prompt">
          <button onClick={() => { setShowTrialPrompt(false); localStorage.setItem('dismissed_website_trial', 'true'); }} className="absolute top-3 right-3 text-[#64748B] hover:text-white"><X size={16} /></button>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.4)]">
              <Globe size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold font-syne">Do you have a website?</p>
              <p className="text-[#9CA3AF] text-sm">If not, start a <span className="text-[#A78BFA] font-bold">FREE 14-day trial</span> today! Build a professional website in minutes.</p>
            </div>
            <button onClick={() => navigate('/create-website')} className="px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm whitespace-nowrap" data-testid="start-free-trial-btn">
              Start Free Trial
            </button>
          </div>
        </motion.div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="vendor-dashboard-title">Vendor Dashboard</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{vendor?.business_name || 'Manage your vendor profile and services'}</p>
        </div>
        <div className="hidden sm:flex items-center gap-3">
          <div className="px-4 py-2.5 bg-[#2D2D52]/40 border border-white/10 rounded-xl text-sm text-[#9CA3AF]">
            {greeting()}, <span className="text-white font-medium">{user?.name || user?.email?.split('@')[0]}</span>
          </div>
          {vendor && (
            <Link to={`/vendor/${vendor?.vendor_id}`}>
              <button className="px-4 py-2.5 rounded-xl text-sm font-medium text-[#A78BFA] border border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10 transition-all">
                View Public Profile
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Profile completion moved to the Home tab */}

      {/* ✨ Gold flashing alert when an inquiry needs attention */}
      {(pendingInquiries > 0 || pendingCustomOrders > 0) && (
        <Link
          to="/dashboard/vendor/inquiries"
          className="block relative rounded-2xl overflow-hidden border-2 border-yellow-300/60 bg-gradient-to-r from-yellow-400/20 via-amber-400/15 to-yellow-300/20 p-4 hover:border-yellow-300 transition-all group animate-pulse-soft"
          data-testid="overview-inquiry-alert"
          style={{ animation: 'inquiry-glow 1.8s ease-in-out infinite' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-300/0 via-yellow-300/10 to-yellow-300/0 group-hover:via-yellow-300/20 transition-all" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-yellow-400 flex items-center justify-center shadow-[0_0_20px_rgba(252,211,77,0.6)] flex-shrink-0">
              <Bell size={20} className="text-[#0E0E23]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-yellow-100 font-bold font-syne text-base">
                {pendingInquiries > 0 && `${pendingInquiries} new inquir${pendingInquiries === 1 ? 'y' : 'ies'}`}
                {pendingInquiries > 0 && pendingCustomOrders > 0 && ' · '}
                {pendingCustomOrders > 0 && `${pendingCustomOrders} active order${pendingCustomOrders === 1 ? '' : 's'}`}
              </p>
              <p className="text-yellow-100/80 text-xs">Tap to review and respond.</p>
            </div>
            <ArrowRight size={18} className="text-yellow-200 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </div>
          <style>{`
            @keyframes inquiry-glow {
              0%, 100% { box-shadow: 0 0 0 0 rgba(252, 211, 77, 0.4); }
              50% { box-shadow: 0 0 24px 6px rgba(252, 211, 77, 0.45); }
            }
          `}</style>
        </Link>
      )}

      {/* 🟡 Gold flashy earnings hero + live active orders list */}
      <VendorEarningsPulse />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, change: 8.2, positive: true, to: '/dashboard/vendor/orders' },
          { label: 'Job Inquiries', value: pendingInquiries, icon: Briefcase, badge: pendingInquiries > 0, to: '/dashboard/vendor/inquiries' },          { label: 'Completed', value: stats?.completed_bookings || 0, icon: CheckCircle, change: 12.5, positive: true, to: '/dashboard/vendor/orders' },
          { label: 'Total Earnings', value: `$${stats?.total_earnings || 0}`, icon: DollarSign, change: 5.3, positive: true, to: '/dashboard/vendor/earnings' },
        ].map((stat, i) => (
          <Link
            to={stat.to || '#'}
            key={i}
            data-testid={`vendor-metric-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <motion.div className="relative bg-gradient-to-br from-[#8B5CF6] via-[#6D28D9] to-[#3B82F6] rounded-2xl p-6 overflow-hidden group hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all duration-300 cursor-pointer" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-4 -translate-x-4" />
              {stat.badge && (
                <span className="absolute top-3 right-3 z-10 px-2 py-0.5 rounded-full bg-yellow-300 text-[#0E0E23] text-[9px] font-bold animate-pulse">NEW</span>
              )}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm">
                    <stat.icon size={20} className="text-white" strokeWidth={2} />
                  </div>
                  <ArrowRight size={18} className="text-white/50 group-hover:text-white/80 group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white/70 font-medium">{stat.label}</p>
                  {stat.change !== undefined && (
                    <span className={`flex items-center gap-0.5 text-xs font-bold ${stat.positive ? 'text-[#86EFAC]' : 'text-[#FCA5A5]'}`}>
                      {stat.positive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                      {stat.change}%
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          </Link>
        ))}
      </div>

      {vendor && (
        <GlassCard className="p-6" data-testid="vendor-profile-status">
          <h2 className="text-lg font-semibold text-white font-syne mb-4">Profile Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
              <span className="text-[#9CA3AF] text-sm">Membership</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                vendor.membership_level === 'elite' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white border-transparent'
                : vendor.membership_level === 'premium' ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30'
                : 'bg-white/10 text-[#9CA3AF] border-white/10'
              }`}>{vendor.membership_level?.charAt(0).toUpperCase() + vendor.membership_level?.slice(1) || 'Standard'}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
              <span className="text-[#9CA3AF] text-sm">Rating</span>
              <span className="text-white text-sm flex items-center gap-1">
                <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                {vendor.rating?.toFixed(1) || 'New'} ({vendor.review_count || 0})
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
              <span className="text-[#9CA3AF] text-sm">Jobs Completed</span>
              <span className="text-white text-sm font-semibold">{vendor.jobs_completed || 0}</span>
            </div>
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-6" data-testid="vendor-active-orders">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white font-syne">Active Orders</h2>
          <Link to="/dashboard/vendor/orders" className="text-[#A78BFA] text-sm hover:text-white transition-colors">View All</Link>
        </div>
        {(() => {
          const activeOrders = bookings.filter(b => ['booked', 'in_progress', 'delivered'].includes(b.status));
          if (activeOrders.length === 0) {
            return (
              <div className="text-center py-6">
                <Calendar size={32} className="mx-auto mb-3 text-[#64748B]" />
                <p className="text-[#9CA3AF] text-sm">No active orders right now</p>
                <p className="text-[#64748B] text-xs mt-1">New orders will appear here when customers book your services</p>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              {activeOrders.slice(0, 5).map((booking) => (
                <div key={booking.booking_id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${booking.status === 'booked' ? 'bg-[#FCD34D]' : booking.status === 'in_progress' ? 'bg-[#93C5FD]' : 'bg-[#C4B5FD]'}`} />
                    <div>
                      <p className="text-white text-sm font-medium">{booking.service_title || 'Service'}</p>
                      <p className="text-[#64748B] text-xs">{booking.consumer_name || 'Customer'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      booking.status === 'booked' ? 'bg-yellow-500/15 text-[#FCD34D]' :
                      booking.status === 'in_progress' ? 'bg-blue-500/15 text-[#93C5FD]' :
                      'bg-purple-500/15 text-[#C4B5FD]'
                    }`}>{booking.status === 'booked' ? 'New' : booking.status === 'in_progress' ? 'In Progress' : 'Delivered'}</span>
                    <p className="text-[#A78BFA] text-sm font-semibold mt-1">${booking.price}</p>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </GlassCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <GlassCard className="p-5">
          <p className="text-[#9CA3AF] text-xs font-medium mb-2">Completion Rate</p>
          <div className="flex items-end gap-3">
            <span className="text-2xl font-bold text-white">{stats?.total_bookings > 0 ? Math.round(((stats?.completed_bookings || 0) / stats.total_bookings) * 100) : 0}%</span>
            <span className="text-[#86EFAC] text-xs mb-1">{stats?.completed_bookings || 0} of {stats?.total_bookings || 0} orders</span>
          </div>
          <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] rounded-full" style={{ width: `${stats?.total_bookings > 0 ? ((stats?.completed_bookings || 0) / stats.total_bookings) * 100 : 0}%` }} />
          </div>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-[#9CA3AF] text-xs font-medium mb-2">Platform Fees</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-white">10%</span>
            <span className="text-[#9CA3AF] text-xs">seller fee per transaction</span>
          </div>
          <p className="text-[#64748B] text-[10px] mt-2">Buyers pay 0% — what they see is what they pay. Vendor seller fee is 10%.</p>
        </GlassCard>
      </div>
    </div>
  );
}
